// Shopify rich-text JSON ↔ Portable Text. Sanity stores Portable Text; rendering converts it
// back to Shopify JSON so convertSchemaToHtml produces the same HTML as the Shopify content.

type ShopifyTextNode = {
  type: 'text';
  value: string;
  bold?: boolean;
  italic?: boolean;
};

type ShopifyLinkNode = {
  type: 'link';
  url: string;
  title: string | null;
  target: string | null;
  children: ShopifyInlineNode[];
};

type ShopifyInlineNode = ShopifyTextNode | ShopifyLinkNode;

type ShopifyListItemNode = { type: 'list-item'; children: ShopifyInlineNode[] };

type ShopifyBlockNode =
  | { type: 'paragraph'; children: ShopifyInlineNode[] }
  | { type: 'heading'; level: number; children: ShopifyInlineNode[] }
  | { type: 'list'; listType: string; children: ShopifyListItemNode[] };

export type PortableTextSpan = {
  _type: 'span';
  _key: string;
  text: string;
  marks: string[];
};

export type PortableTextLinkDef = {
  _key: string;
  _type: 'link';
  href: string;
  title?: string;
  target?: string;
};

export type PortableTextBlock = {
  _type: 'block';
  _key: string;
  style: string;
  listItem?: 'bullet' | 'number';
  level?: number;
  // Marks a list that directly follows another list of the same type, which Portable Text would merge.
  listStart?: true;
  markDefs: PortableTextLinkDef[];
  children: PortableTextSpan[];
};

/** Thrown internally for node shapes the converter doesn't support. */
class UnsupportedNode extends Error {}

function spanMarks(node: ShopifyTextNode, linkKey?: string): string[] {
  const marks: string[] = linkKey ? [linkKey] : [];
  if (node.bold) marks.push('strong');
  if (node.italic) marks.push('em');
  return marks;
}

function convertInline(
  nodes: ShopifyInlineNode[],
  block: PortableTextBlock,
): void {
  for (const node of nodes) {
    if (node.type === 'text') {
      block.children.push({
        _type: 'span',
        _key: `s${block.children.length}`,
        text: node.value,
        marks: spanMarks(node),
      });
    } else if (node.type === 'link') {
      const def: PortableTextLinkDef = {
        _key: `l${block.markDefs.length}`,
        _type: 'link',
        href: node.url,
        ...(node.title !== null && node.title !== undefined
          ? { title: node.title }
          : {}),
        ...(node.target !== null && node.target !== undefined
          ? { target: node.target }
          : {}),
      };
      block.markDefs.push(def);
      for (const child of node.children) {
        if (child.type !== 'text') throw new UnsupportedNode(child.type);
        block.children.push({
          _type: 'span',
          _key: `s${block.children.length}`,
          text: child.value,
          marks: spanMarks(child, def._key),
        });
      }
    } else {
      throw new UnsupportedNode((node as { type: string }).type);
    }
  }
}

function makeBlock(
  key: string,
  style: string,
  listItem: PortableTextBlock['listItem'],
  children: ShopifyInlineNode[],
): PortableTextBlock {
  const block: PortableTextBlock = {
    _type: 'block',
    _key: key,
    style,
    ...(listItem ? { listItem, level: 1 } : {}),
    markDefs: [],
    children: [],
  };
  convertInline(children, block);
  return block;
}

/**
 * Portable Text blocks, or null for invalid JSON, a non-root value or an unsupported node.
 * The corpus test proves the blocks re-serialize to the exact input bytes.
 */
export function shopifyRichTextToPortableText(
  value: string,
): PortableTextBlock[] | null {
  let root: { type?: string; children?: ShopifyBlockNode[] };
  try {
    root = JSON.parse(value);
  } catch {
    return null;
  }
  if (root?.type !== 'root' || !Array.isArray(root.children)) return null;

  const blocks: PortableTextBlock[] = [];
  // Any failure (unsupported node, malformed shape, converter bug) returns null.
  try {
    for (const node of root.children) {
      if (node.type === 'paragraph') {
        blocks.push(
          makeBlock(`b${blocks.length}`, 'normal', undefined, node.children),
        );
      } else if (node.type === 'heading') {
        blocks.push(
          makeBlock(
            `b${blocks.length}`,
            `h${node.level}`,
            undefined,
            node.children,
          ),
        );
      } else if (node.type === 'list') {
        const listItem =
          node.listType === 'ordered'
            ? 'number'
            : node.listType === 'unordered'
              ? 'bullet'
              : null;
        if (!listItem) throw new UnsupportedNode(node.listType);
        const previous = blocks[blocks.length - 1];
        const followsSameList = previous?.listItem === listItem;
        node.children.forEach((item, itemIndex) => {
          if (item.type !== 'list-item') throw new UnsupportedNode(item.type);
          const block = makeBlock(
            `b${blocks.length}`,
            'normal',
            listItem,
            item.children,
          );
          if (followsSameList && itemIndex === 0) block.listStart = true;
          blocks.push(block);
        });
      } else {
        throw new UnsupportedNode((node as { type: string }).type);
      }
    }
    return blocks;
  } catch {
    return null;
  }
}

// Shopify omits bold/italic keys entirely when false — reproduced here so the
// serialization stays byte-identical (key insertion order is the wire order).
function textNode(span: PortableTextSpan): ShopifyTextNode {
  const marks = span.marks ?? [];
  return {
    type: 'text',
    value: span.text ?? '',
    ...(marks.includes('strong') ? { bold: true } : {}),
    ...(marks.includes('em') ? { italic: true } : {}),
  };
}

function inlineChildren(block: PortableTextBlock): ShopifyInlineNode[] {
  const defsByKey = new Map(
    (block.markDefs ?? []).map((def) => [def._key, def]),
  );
  const out: ShopifyInlineNode[] = [];
  let openLinkKey: string | null = null;
  for (const span of block.children ?? []) {
    const linkKey = (span.marks ?? []).find((mark) => defsByKey.has(mark));
    const text = textNode(span);
    const prev = out[out.length - 1];
    if (!linkKey) {
      out.push(text);
      openLinkKey = null;
    } else if (linkKey === openLinkKey && prev?.type === 'link') {
      // Consecutive spans on the same link (e.g. plain + bold segments)
      // collapse back into one link node.
      prev.children.push(text);
    } else {
      const def = defsByKey.get(linkKey)!;
      out.push({
        type: 'link',
        url: def.href ?? '',
        title: def.title ?? null,
        target: def.target ?? null,
        children: [text],
      });
      openLinkKey = linkKey;
    }
  }
  return out;
}

/** Serializes Portable Text back to Shopify's exact rich-text JSON string. */
export function portableTextToShopifyRichText(
  blocks: PortableTextBlock[],
): string {
  const children: ShopifyBlockNode[] = [];
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index];
    if (block.listItem) {
      const listType = block.listItem === 'number' ? 'ordered' : 'unordered';
      const items: ShopifyListItemNode[] = [];
      const start = index;
      while (
        index < blocks.length &&
        blocks[index].listItem === block.listItem &&
        (index === start || !blocks[index].listStart)
      ) {
        items.push({
          type: 'list-item',
          children: inlineChildren(blocks[index]),
        });
        index += 1;
      }
      children.push({ type: 'list', listType, children: items });
    } else {
      const heading = /^h([1-6])$/.exec(block.style || 'normal');
      children.push(
        heading
          ? {
              type: 'heading',
              level: Number(heading[1]),
              children: inlineChildren(block),
            }
          : { type: 'paragraph', children: inlineChildren(block) },
      );
      index += 1;
    }
  }
  return JSON.stringify({ type: 'root', children });
}
